import { colors, typography, spacing } from '@mehilainen/mds-tokens';
import { formHelperTextClasses, inputBaseClasses, inputLabelClasses, svgIconClasses, } from '@mui/material';
import { mdsStyleClasses } from '../../mdsStyleClasses';
const { baseTypography } = typography;
//Common styles for all MUI input fields
export const getThemeFormElements = (tokens) => {
    const inputTokens = tokens.uiElementInputFields;
    const useStandardLabel = tokens.values.inputLabelPlacement === 'standard';
    return {
        MuiFormControl: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                className: useStandardLabel
                    ? mdsStyleClasses.standardTextField
                    : mdsStyleClasses.notchedTextField,
            },
            styleOverrides: {
                root: ({ ownerState }) => {
                    var _a, _b, _c;
                    if (ownerState.variant !== 'outlined') {
                        return;
                    }
                    if (!((_a = ownerState.className) === null || _a === void 0 ? void 0 : _a.includes(`${mdsStyleClasses.standardTextField}`))) {
                        if (ownerState.size === 'small') {
                            return {
                                '& label': {
                                    fontSize: inputTokens.sizeSmall.notchedLabelFontSize,
                                    fontWeight: inputTokens.sizeSmall.notchedLabelFontWeight,
                                    lineHeight: baseTypography.height.Small,
                                    [`&:not([class*="${inputLabelClasses.shrink}"])`]: {
                                        fontSize: inputTokens.sizeSmall.contentFontSize,
                                        fontWeight: baseTypography.weight.Regular,
                                        lineHeight: baseTypography.height.Small,
                                    },
                                },
                                '& fieldset': {
                                    paddingLeft: ((_b = inputTokens.common.paddingLeft) !== null && _b !== void 0 ? _b : 16) - 5 + 'px',
                                    '& legend': {
                                        fontSize: inputTokens.sizeSmall.notchedLabelFontSize,
                                        fontWeight: inputTokens.sizeSmall.notchedLabelFontWeight,
                                        lineHeight: baseTypography.height.Small,
                                    },
                                },
                            };
                        }
                        return {
                            '& label': {
                                fontSize: inputTokens.sizeMedium.notchedLabelFontSize,
                                fontWeight: inputTokens.sizeMedium.notchedLabelFontWeight,
                                lineHeight: baseTypography.height.Small,
                                [`&:not([class*="${inputLabelClasses.shrink}"])`]: {
                                    fontSize: inputTokens.sizeMedium.contentFontSize,
                                    fontWeight: baseTypography.weight.Regular,
                                    lineHeight: baseTypography.height.Small,
                                },
                            },
                            '& fieldset': {
                                paddingLeft: ((_c = inputTokens.common.paddingLeft) !== null && _c !== void 0 ? _c : 16) - 5 + 'px',
                                '& legend': {
                                    fontSize: inputTokens.sizeMedium.notchedLabelFontSize,
                                    fontWeight: inputTokens.sizeMedium.notchedLabelFontWeight,
                                    lineHeight: baseTypography.height.Small,
                                },
                            },
                        };
                    }
                    return {
                        // NOTE: MUI Textfield does not allow variants so this is a custom variant via class
                        [`&.${mdsStyleClasses.standardTextField}`]: {
                            '& > label': {
                                position: 'unset',
                                paddingBottom: '4px',
                                WebkitTransform: 'unset',
                                MozTransform: 'unset',
                                msTransform: 'unset',
                                transform: 'unset',
                                fontSize: ownerState.size === 'small'
                                    ? inputTokens.sizeSmall.labelFontSize
                                    : inputTokens.sizeMedium.labelFontSize,
                                fontWeight: ownerState.size === 'small'
                                    ? inputTokens.sizeSmall.labelFontWeight
                                    : inputTokens.sizeMedium.labelFontWeight,
                                lineHeight: baseTypography.height.Medium,
                            },
                            [`& > [class*="${formHelperTextClasses.root}"]`]: {
                                // Outlined helpertext has default margin
                                marginLeft: 0,
                            },
                            '& legend': {
                                // Prevent notch
                                maxWidth: '0.01px !important',
                            },
                        },
                    };
                },
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
                minRows: 3,
                maxRows: 8,
                className: useStandardLabel
                    ? mdsStyleClasses.standardTextField
                    : mdsStyleClasses.notchedTextField,
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    var _a;
                    if ((_a = ownerState.className) === null || _a === void 0 ? void 0 : _a.includes(`${mdsStyleClasses.standardTextField}`)) {
                        return;
                    }
                    // calculating the reguired margin from top for notched variant label when it's centered
                    const sizeTokens = inputTokens[ownerState.size === 'small' ? 'sizeSmall' : 'sizeMedium'];
                    const convertToNumber = (token) => token ? Number(token.replace('rem', '')) : undefined;
                    const elementHeight = convertToNumber(sizeTokens.height);
                    const contentHeight = convertToNumber(sizeTokens.contentFontSize);
                    const labelHeight = convertToNumber(sizeTokens.notchedLabelFontSize);
                    if (elementHeight && contentHeight && labelHeight) {
                        const marginTop = (elementHeight - contentHeight) / 2;
                        const marginTopShrink = -(labelHeight / 2);
                        return {
                            transform: `translate(${inputTokens.common.paddingLeft}px, ${marginTopShrink}rem) scale(1)`,
                            [`&:not([class*="${inputLabelClasses.shrink}"])`]: {
                                transform: `translate(${inputTokens.common.paddingLeft}px, ${marginTop}rem) scale(1)`,
                            },
                        };
                    }
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    color: inputTokens.common.labelColor,
                    '&[class*="Mui-focused"]:not([class*="Mui-error"])': {
                        color: inputTokens.common.labelFocusedColor,
                    },
                    '&[class*="Mui-disabled"]': {
                        color: colors.Gray400,
                    },
                },
                filled: {
                    color: inputTokens.common.labelFilledColor,
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                input: {
                    lineHeight: baseTypography.height.Medium,
                    '&::placeholder': {
                        opacity: 1,
                        color: colors.Gray600,
                    },
                },
                root: ({ ownerState }) => {
                    if (ownerState.multiline) {
                        return;
                    }
                    return {
                        minHeight: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.height
                            : inputTokens.sizeMedium.height,
                        // For some reason error- css selector was not working
                        '&.Mui-error': {
                            [`& > [class*="${svgIconClasses.root}"]`]: {
                                color: colors.Error500,
                            },
                        },
                    };
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    const color = !ownerState.error && ownerState.focused
                        ? { color: theme.palette.primary.main }
                        : {};
                    return Object.assign({ fontSize: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.assistiveTextSize
                            : inputTokens.sizeMedium.assistiveTextSize, fontWeight: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.assistiveTextWeight
                            : inputTokens.sizeMedium.assistiveTextWeight, lineHeight: baseTypography.height.Medium, marginLeft: 0, marginTop: spacing[200].px }, color);
                },
            },
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    '&:hover:not([class*="Mui-disabled"]):before': {
                        borderColor: colors.Primary500,
                    },
                    '&::before': {
                        borderColor: inputTokens.common.borderColor,
                    },
                },
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ ownerState }) => {
                    return {
                        fontSize: ownerState.size === 'small'
                            ? inputTokens.sizeSmall.contentFontSize
                            : inputTokens.sizeMedium.contentFontSize,
                        lineHeight: baseTypography.height.Small,
                        backgroundColor: colors.White,
                        color: colors.Gray900,
                        borderColor: inputTokens.common.borderColor,
                        borderRadius: inputTokens.common.borderRadius,
                        // textarea stylings
                        [`&[class*="${inputBaseClasses.multiline}"]`]: {
                            padding: spacing[400].px + ' ' + spacing[500].px,
                            [`& [class*="${inputBaseClasses.input}"]`]: {
                                padding: 0,
                            },
                        },
                        '&[class*="Mui-error"]': {
                            borderColor: colors.Error500,
                            color: colors.Error500,
                            ':not(:hover):not(active)': {
                                backgroundColor: inputTokens.common.errorBackground,
                            },
                        },
                        '&:hover:not([class*="Mui-error"]):not([class*="Mui-disabled"])': {
                            borderColor: colors.Primary500,
                        },
                        '&[class*="Mui-focused"]:not([class*="Mui-error"]):not([class*="Mui-disabled"])': {
                            borderColor: colors.Primary500,
                        },
                        '&[class*="Mui-disabled"]': {
                            borderColor: colors.Gray300,
                            backgroundColor: colors.Gray100,
                        },
                    };
                },
                // NOTE: No idea why this exists in dom if notched is false, but this actually contains all the needed border rules
                notchedOutline: {
                    borderColor: 'inherit !important', // Otherwise default bordercolors just override custom ones given at root
                    //borderWidth: '1px !important', // Active input has width 2 px on default
                },
                input: {
                    padding: `${spacing[300].px} ${inputTokens.common.paddingLeft}px`,
                },
            },
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: colors.Gray600,
                },
            },
        },
    };
};
